"use strict";
/* eslint-disable max-classes-per-file */
Object.defineProperty(exports, "__esModule", { value: true });
var DataType;
(function (DataType) {
    DataType[DataType["String"] = 0] = "String";
    DataType[DataType["Number"] = 1] = "Number";
    DataType[DataType["Boolean"] = 2] = "Boolean";
    DataType[DataType["Function"] = 3] = "Function";
    DataType[DataType["Null"] = 4] = "Null";
    DataType[DataType["Undefined"] = 5] = "Undefined";
    DataType[DataType["Date"] = 6] = "Date";
    DataType[DataType["Array"] = 7] = "Array";
    DataType[DataType["Object"] = 8] = "Object";
    DataType[DataType["Unknown"] = 9] = "Unknown";
})(DataType = exports.DataType || (exports.DataType = {}));
exports.toDataType = (data) => {
    if (data === null) {
        return DataType.Null;
    }
    if (data === undefined) {
        return DataType.Undefined;
    }
    if (data === true || data === false) {
        return DataType.Boolean;
    }
    if (Array.isArray(data)) {
        return DataType.Array;
    }
    if (data instanceof Date) {
        return DataType.Date;
    }
    if (typeof data === 'string' || data instanceof String) {
        return DataType.String;
    }
    if (typeof data === 'number' || !Number.isNaN(+data)) {
        return DataType.Number;
    }
    if (typeof data === 'function' || data instanceof Function) {
        return DataType.Function;
    }
    if (typeof data === 'object') {
        return DataType.Object;
    }
    return DataType.Unknown;
};
exports.mapper = (data, mapping = {}, key, contextualKey) => {
    const dataType = exports.toDataType(data);
    if (mapping.custom) {
        const funcs = mapping.custom.filter(([useThisMapping]) => useThisMapping(data, dataType, key, contextualKey));
        if (funcs.length) {
            return funcs.reduce((acc, [, fn]) => fn(acc, dataType, key, contextualKey), data);
        }
    }
    const mappingFunc = mapping[dataType];
    if (mappingFunc) {
        const result = mappingFunc(data, key, contextualKey);
        // in short, a transform of:
        //
        // (obj, key) => { key === 'something' ? 12 : obj }
        //
        // when obj is an Object, we should recurse into it
        if (result !== data || exports.toDataType(result) !== DataType.Object) {
            return result;
        }
    }
    switch (dataType) {
        case DataType.Array:
            // have to use Array.from because of array-like objects
            return Array.from(data).map((val, i) => exports.mapper(val, mapping, key, contextualKey ? `${contextualKey}[${i}]` : `[${i}]`));
        case DataType.Object: {
            const output = {};
            if (data.constructor.name !== 'Object') {
                console.warn(`Iterating over a ${data.constructor.name}, which will lose your class instance type`);
            }
            for (const [key, value] of Object.entries(data)) {
                output[key] = exports.mapper(value, mapping, key, contextualKey ? `${contextualKey}.${key}` : key);
            }
            return output;
        }
        case DataType.String:
        case DataType.Number:
        case DataType.Boolean:
        case DataType.Function:
        case DataType.Null:
        case DataType.Undefined:
        case DataType.Unknown:
        default:
            return data;
    }
};
const buildNestedPropertyAccessors = (obj, exclude = [], ctx = []) => {
    const props = [];
    if (exports.toDataType(obj) !== DataType.Object) {
        return [];
    }
    for (const [key] of Object.entries(obj)) {
        const nested = buildNestedPropertyAccessors(obj[key], exclude, ctx.concat(key));
        if (nested.length) {
            props.push(...nested);
        }
        else {
            props.push(ctx.concat(key));
        }
    }
    return props;
};
const getProperty = (obj, accessor) => accessor.reduce((obj, property) => {
    if (obj[property] !== undefined) {
        return obj[property];
    }
    throw new Error(`@lcdev/mapper could not access property '${accessor.join('.')}'`);
}, obj);
const setProperty = (obj, accessor, value, createObjs = false) => {
    if (accessor.length === 1) {
        obj[accessor[0]] = value;
    }
    else if (accessor.length > 1) {
        if (createObjs && obj[accessor[0]] === undefined) {
            obj[accessor[0]] = {};
        }
        setProperty(obj[accessor[0]], accessor.slice(1), value, createObjs);
    }
    else if (accessor.length === 0) {
        Object.assign(obj, value);
    }
};
exports.structuredMapper = (data, mapping) => {
    if (typeof mapping === 'function') {
        return exports.structuredMapper(data, { map: mapping });
    }
    if (mapping === true) {
        return exports.structuredMapper(data, { map: (v) => v });
    }
    if (mapping === false) {
        return undefined;
    }
    if (Array.isArray(mapping)) {
        if (mapping.length !== 1) {
            throw new Error('Array mapping shorthand should be [bool | {} | v => v] syntax');
        }
        return exports.structuredMapper(data, {
            array: true,
            map: (v) => exports.structuredMapper(v, mapping[0]),
        });
    }
    if ('map' in mapping) {
        if (data === undefined && 'optional' in mapping && mapping.optional) {
            return undefined;
        }
        if (data === null && 'nullable' in mapping && mapping.nullable) {
            if ('fallback' in mapping) {
                return mapping.fallback;
            }
            return null;
        }
        if (mapping.array) {
            if (exports.toDataType(data) !== DataType.Array) {
                throw new Error('received an array mapping, but input data was not');
            }
            return data.map(v => mapping.map(v, exports.toDataType(v)));
        }
        return mapping.map(data, exports.toDataType(data));
    }
    if (data === undefined) {
        throw new Error('Cannot do a structured map on undefined');
    }
    else if (typeof data !== 'object') {
        throw new Error(`Cannot do a structured map on a non-object (${data})`);
    }
    // get mappings without the trailing .map or .optional
    const exclude = [
        'map',
        'fallback',
        'optional',
        'nullable',
        'rename',
        'array',
        'additionalProperties',
    ];
    const mappings = buildNestedPropertyAccessors(mapping)
        .map(prop => {
        if (exclude.includes(prop[prop.length - 1])) {
            return prop.slice(0, -1);
        }
        return prop;
    })
        .filter(v => v.length > 0);
    const output = {};
    if (mapping.additionalProperties) {
        Object.assign(output, data);
    }
    mappings
        .map(prop => [prop, getProperty(mapping, prop)])
        .forEach(([prop, mapping]) => {
        let input;
        try {
            // when getting the property, we ignore 'flatten'
            input = getProperty(data, prop.filter((p) => p !== 'flatten'));
        }
        catch (err) {
            if (!mapping.optional) {
                throw err;
            }
            else if ('fallback' in mapping) {
                setProperty(output, prop, mapping.fallback, true);
            }
            return;
        }
        if (mapping.rename) {
            prop.splice(-1, 1, mapping.rename);
        }
        // when setting the property, we fold 'flatten' upwards
        const destProp = prop.reduce((acc, p) => {
            if (p === 'flatten')
                return acc.slice(0, -1);
            return acc.concat(p);
        }, []);
        setProperty(output, destProp, exports.structuredMapper(input, mapping), true);
    });
    return output;
};
class Rename {
    constructor(to) {
        this.to = to;
    }
}
exports.Rename = Rename;
class Transform {
    constructor(fn) {
        this.fn = fn;
    }
}
exports.Transform = Transform;
exports.rename = (to) => new Rename(to);
exports.transform = (fn) => new Transform(fn);
exports.extract = (body, extraction) => {
    const output = {};
    if (typeof body !== 'object')
        return body;
    if (body === null)
        return null;
    // for [...] and [{ ...mapping }]
    const isArrMap = Array.isArray(extraction) && extraction.length === 1 && typeof extraction[0] === 'object';
    if (!isArrMap && Array.isArray(extraction)) {
        if (extraction.length > 1) {
            throw new Error('It is invalid to pass an array with length > 1 as a property of extract');
        }
        // foo: [] or foo: [false] is equivalent to false
        if (extraction.length === 0 || extraction[0] === false) {
            extraction = false;
        }
    }
    if (extraction === false || extraction === null || extraction === undefined) {
        return undefined;
    }
    if (Array.isArray(body)) {
        if (isArrMap) {
            return body.map(v => exports.extract(v, extraction[0]));
        }
        return body;
    }
    // we know that body is not an array
    if (isArrMap)
        return body;
    for (const [field, extractField] of Object.entries(extraction)) {
        // either [{ bar: true }] or ['fieldA', 'fieldB']
        if (Array.isArray(extractField)) {
            // this is when mapping is [{ bar: true }]
            // [{bar:1,baz:2},{bar:2,baz:3}] -> [{bar:1},{bar:2}]
            const isObjectMapping = extractField.some(v => typeof v !== 'string');
            if (isObjectMapping) {
                if (extractField.length !== 1) {
                    throw new Error('Bad [{}] syntax for extract');
                }
                if (!Array.isArray(body[field])) {
                    continue;
                }
                // [{ bar: true }]
                const extractor = extractField[0];
                // map over fields, recursing
                output[field] = body[field].map((v) => exports.extract(v, extractor));
            }
            else {
                if (typeof body[field] !== 'object') {
                    continue;
                }
                output[field] = {};
                // this is when ['fieldA', 'fieldB']
                for (const arrField of extractField) {
                    output[field][arrField] = body[field][arrField];
                }
            }
        }
        else if (extractField === true) {
            // { bar: true } means return anything in bar
            output[field] = body[field];
        }
        else if (extractField === false) {
            continue;
        }
        else if (extractField instanceof Rename) {
            output[extractField.to] = body[field];
        }
        else if (extractField instanceof Transform) {
            output[field] = extractField.fn(body[field]);
        }
        else {
            // { foo: { bar: ... } } - recurse into foo
            output[field] = exports.extract(body[field], extractField);
        }
    }
    return output;
};
//# sourceMappingURL=index.js.map